import { Devices } from '../../hooks/use-device';
import { AVAILABLE_WORLDS } from '../../languages/constants';
import { storageKeys } from '../../utils/browser-storage/storage-keys';

export type ABTestGroupName = keyof typeof AB_TEST_GROUPS;
export type ABTestKey = keyof typeof AB_TESTS;
export type StatsigABTestKey = keyof typeof STATSIG_AB_TESTS;
export type ABTestProps = {
    gaDimension: string;
    // String type uses for backward compatibility. In the future in ideal world this prop should have type only from AB_TESTS keys
    gaTestName: ABTestKey | string;
    unleashKey: string;
    hardApplyParam?: string;
};
export type ABTestData = ABTestProps & {
    groupActiveTest?: ABTestKey | '';
};
export type StatsigABTestDataType = {
    statsigKey: string;
    // Present ⇒ read from a layer (getLayer(layerName).get(statsigKey)) instead of getExperiment
    layerName?: string;
    limitedWorlds?: AVAILABLE_WORLDS | AVAILABLE_WORLDS[];
    allowedDevices?: Devices[];
};

type ABTestGroups = { [key in ABTestGroupName]: Array<ABTestKey> };
type ABTests = { [key in ABTestKey]: ABTestProps };
type StatsigABTests = { [key in StatsigABTestKey]: StatsigABTestDataType };

enum AB_TEST_GROUPS {
    MaterialPageDesktopABTestsGroup = 'Group for AB tests on the material page for the desktop view',
    MaterialPageDesktopABTestsGroup2 = 'The 2nd group for AB tests on the material page for the desktop view',
    MaterialPageMobileABTestsGroup = 'Group for AB tests on the material page for the mobile view',
    MaterialPageABTestsGroup = 'Group for AB tests on the material page for all platforms'
}

enum AB_TESTS {}

export const EXISTED_AB_TEST_GROUPS: ABTestGroups = {
    MaterialPageDesktopABTestsGroup: [],
    MaterialPageDesktopABTestsGroup2: [],
    MaterialPageMobileABTestsGroup: [],
    MaterialPageABTestsGroup: []
};

const {
    testSegment1,
    testSegment2,
    testSegment3,
    testSegment4,
    testSegment5,
    testSegment6,
    testSegment7,
    testSegment8,
    testSegment9,
    testSegment10,
    testSegment11,
    testSegment12
} = storageKeys.abtests;

export const EXISTED_AB_TESTS: ABTests = {};

enum STATSIG_AB_TESTS {
    didYouMean = 'Did You Mean interpretation suggestion',
    polotnoEditorTest = 'Tesing new Worksheet based on some canvas editor tool called Polotno',
    aiMaterialCustomizer = 'Testing of the new Eduki_Material_Customizer tool for a selected group of test users',
    blurPreview = 'Blur previews abtest',
    myMaterialBundleDisplay = 'My material bundle display',
    searchListHighlight = 'Highlight search query matches in list-view title/description for DACH web + tablet',
    preselectedPaymentMethod = 'Previously used payment method to be pre-selected in checkout',
    aiStudioRollout = 'Replace legacy AI-Tools + Customizer entry points with the new AI Studio workspace (Phase-1 internal prod A/B)',
    bundleSlider = 'New Bundle slider visualization on material card',
    aiWorksheetCreator = 'Testing of the new Complementary_worksheet tool for a selected group of test users',
    urgencySignal = 'Show urgency signal on PDP for materials with 10+ purchases in last 30 days',
    upsellModuleThankPage = 'Upsell module on the free material thank you page',
    adaptAiToolExperiment = 'Backdoor AB test for AI tool adaptation experiment',
    pickedForYou = 'Show a "Picked for you" personalized recommendations row above the thematic section on PDP for eligible users',
    searchFilterPersistence = 'Keep active search filters when the search query changes'
}

export const EXISTED_STATSIG_AB_TESTS: StatsigABTests = {
    didYouMean: {
        statsigKey: 'did_you_mean',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    polotnoEditorTest: {
        statsigKey: 'polotno_editor_test',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    aiMaterialCustomizer: {
        statsigKey: 'ai_material_customizer',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    blurPreview: {
        statsigKey: 'blur_preview',
        limitedWorlds: AVAILABLE_WORLDS.DE,
        allowedDevices: ['desktop']
    },
    myMaterialBundleDisplay: {
        statsigKey: 'my_material_bundle_display',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    searchListHighlight: {
        statsigKey: 'highlight_search_keyword',
        limitedWorlds: AVAILABLE_WORLDS.DE,
        allowedDevices: ['desktop', 'tablet']
    },
    preselectedPaymentMethod: {
        statsigKey: 'preselected_payment_method'
    },
    aiStudioRollout: {
        statsigKey: 'ai_studio_rollout',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    bundleSlider: {
        statsigKey: 'bundle_slider',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    aiWorksheetCreator: {
        statsigKey: 'ai_worksheet_creator',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - 1406 (remove on either accept or reject) — isolated in material-page layer
    urgencySignal: {
        statsigKey: 'urgency_signal_isolated',
        layerName: 'material-page',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    upsellModuleThankPage: {
        statsigKey: 'upselling_module_thanks_page',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - MCP-1473 (remove on either accept or reject)
    adaptAiToolExperiment: {
        statsigKey: 'adapt_ai_tool_experiment',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - 1374 (remove on either accept or reject) — standalone experiment (additive slider)
    pickedForYou: {
        statsigKey: 'picked_for_you_isolated',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - SRCH-1552 (remove on either accept or reject)
    searchFilterPersistence: {
        statsigKey: 'search_filter_persistence',
        limitedWorlds: AVAILABLE_WORLDS.DE
    }
};
